import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import AppHeader from '../components/AppHeader';
import StarRating from '../components/ui/StarRating';
import {
  Cpu, Search, ShieldCheck, Headphones, TrendingUp, Users, FileText,
  BarChart3, DollarSign, Stethoscope, FileCheck, Shield, ClipboardCheck,
  Scale, BookOpen, Wrench, Truck, Home, Zap, Leaf, Megaphone, Calculator,
  ArrowRight, Coins, Mic, ChevronLeft, ChevronRight, Phone,
  Globe, ExternalLink, ChevronDown, Code, Presentation, ShieldAlert,
  Link2, Puzzle, MessageSquare, Database, Cloud, RefreshCw, LayoutGrid, CheckCircle2, Lock, Plus,
  GitBranch, Trash2, FileSignature
} from 'lucide-react';

// Integration Logos
import twilioLogo from '../assets/twilio.png';
import azureLogo from '../assets/azure.png';
import dynamicsLogo from '../assets/dynamics.png';
import salesforceLogo from '../assets/salesforce.png';
import zohoLogo from '../assets/zoho.png';
import hubspotLogo from '../assets/hubspot.png';

axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon Map for Agents
const iconMap = {
  ShieldCheck, Headphones, TrendingUp, Users, FileText, BarChart3,
  DollarSign, Stethoscope, FileCheck, Shield, ClipboardCheck, Scale,
  BookOpen, Wrench, Truck, Home, Zap, Leaf, Megaphone, Calculator,
  Globe, Code, Presentation, ShieldAlert, Database, GitBranch,
  FileSignature, Bot: Cpu, Search
};

// Icon Map for Integrations
const integrationIconMap = {
  'Communication': MessageSquare,
  'Cloud': Cloud,
  'CRM': RefreshCw,
  'Database': Database,
  'Automation': Zap,
  'Storage': LayoutGrid,
  'Marketing': Megaphone,
};

// Logo URLs for each integration tool
const integrationLogoMap = {
  'Twilio': twilioLogo,
  'Azure Services': azureLogo,
  'Dynamics 365': dynamicsLogo,
  'Salesforce': salesforceLogo,
  'Zoho': zohoLogo,
  'HubSpot': hubspotLogo,
};

// const STATIC_AGENTS = [
//   {
//     id: 'Netsmartz-Project-Pro',
//     name: 'Netsmartz Project Pro',
//     category: 'Computer Vision',
//     description: 'Access our Computer Vision Tool',
//     icon: 'Globe',
//     tier: 'enterprise',
//     credits_per_query: 20,
//     isExternal: true,
//     externalUrl: 'https://projectpro.netsmartz.us/'
//   },
//   {
//     id: 'Precor-Support-Assistant',
//     name: 'Precor Support Assistant',
//     category: 'Healthcare',
//     description: 'Technical service assistant for Precor SCL800 equipment and clinical gym maintenance.',
//     features: ['Maintenance', 'Error Codes', 'SCL800'],
//     icon: 'Stethoscope',
//     tier: 'pro',
//     credits_per_query: 5,
//     isExternal: false,
//     isCustomChat: true
//   },
// ];

const GENERAL_CATEGORIES = [
  'Workflow', 'Productivity', 'Analytics', 'Technology', 'AI Development',
  'Computer Vision', 'Sales', 'Marketing', 'Legal', 'Retail', 'Finance',
  'Operations', 'Customer Service', 'Human Resources', 'Cybersecurity'
];

const INDUSTRY_TREE = {

  "Banking & Finance": {
    "Risk & Compliance": [
      "Banking Risk & Compliance"
    ],
    "Finance": [
      "Finance FP&A"
    ],
    "Analytics": [
      "BI Data Storyteller",
      "Database Query"
    ],
    "Cybersecurity": [
      "Cyber Threat Sentinel"
    ],
    "Operations": [
      "Approval Workflow",
      "Audit & Compliance",
      "Reporting & Analytics",
      "Document Management",
      "SLA Monitoring"
    ]
  },

  "Retail & Ecommerce": {
    "Retail Intelligence": [
      "Retail Demand Forecasting"
    ],
    "Marketing": [
      "Marketing Campaign Optimization"
    ],
    "Customer Experience": [
      "Customer Support (CX)"
    ],
    "Supply Chain": [
      "Supply Chain Resilience"
    ],
    "Analytics": [
      "BI Data Storyteller",
      "Database Query"
    ],
    "Operations": [
      "Deadline Management",
      "Approval Workflow",
      "Reporting & Analytics",
      "Document Management"
    ]
  },

  "Healthcare": {
    "Clinical Operations": [
      "Clinical Care"
    ],
    "Patient Support": [
      "Customer Support (CX)"
    ],
    "Analytics": [
      "BI Data Storyteller",
      "Database Query"
    ],
    "Compliance & Records": [
      "Document Management",
      "Audit & Compliance"
    ],
    "Operations": [
      "SLA Monitoring",
      "Approval Workflow"
    ]
  },

  "SaaS & Technology": {
    "Software Engineering": [
      "Tech Code Review",
      "Software Development"
    ],
    "AI Engineering": [
      "Prompt Generation"
    ],
    "Security": [
      "Cyber Threat Sentinel"
    ],
    "Computer Vision":[
      "Vision AI Agent"
    ],

    "Analytics": [
      "BI Data Storyteller",
      "Database Query"
    ],
    "Operations": [
      "Reporting & Analytics",
      "Approval Workflow",
      "Document Management"
    ]
  },

  "Construction": {
    "Project Management": [
      "Construction Project Manager"
    ],
    "Cost & Risk Management": [
      "Early Cost Overrun Detection"
    ],
    "Compliance": [
      "Subcontractor Compliance Monitor"
    ],
    "Analytics": [
      "BI Data Storyteller"
    ],
    "Operations": [
      "Deadline Management",
      "Approval Workflow",
      "Document Management"
    ]
  },

  "Telecommunications": {
    "Network Planning": [
      "Telecom Network Planner"
    ],
    "Analytics": [
      "BI Data Storyteller",
      "Database Query"
    ],
    "Operations": [
      "SLA Monitoring",
      "Reporting & Analytics",
      "Approval Workflow"
    ]
  },

  "Real Estate": {
    "Property Analytics": [
      "Property AI",
      "Smart Contractor",
      "Interior Design AI"
    ],
    "Analytics": [
      "BI Data Storyteller"
    ],
    "Operations": [
      "Document Management",
      "Approval Workflow",
      "Reporting & Analytics"
    ]
  },

  "Enterprise Functions": {
    "Sales": [
      "Sales"
    ],
    "Marketing": [
      "Marketing Campaign Optimization"
    ],
    "Customer Service": [
      "Customer Support (CX)"
    ],
    "Human Resources": [
      "HR Resume Screening",
      "HR JD & Policy Generator"
    ],
    "Legal": [
      "Legal Contract Review"
    ],
    "Finance": [
      "Finance FP&A"
    ],
    "Cybersecurity": [
      "Cyber Threat Sentinel"
    ],
    "Analytics": [
      "BI Data Storyteller",
      "Database Query"
    ]
  },

  "Productivity & Workflow": {
    "Productivity": [
      "PPT Creation",
      "Document Creation"
    ],
    "Workflow Automation": [
      "Deadline Management",
      "SLA Monitoring",
      "Approval Workflow",
      "Intake Processing",
      "Notification & Alerts",
      "Document Management",
      "Audit & Compliance",
      "Reporting & Analytics"
    ]
  }
};

// Map custom agent category_id to INDUSTRY_TREE industry keys
const CATEGORY_TO_INDUSTRY = {
  cat_banking: "Banking & Finance",
  cat_finance: "Banking & Finance",
  cat_insurance: "Banking & Finance",
  cat_ecommerce: "Retail & Ecommerce",
  cat_supply_chain: "Retail & Ecommerce",
  cat_healthcare: "Healthcare",
  cat_biotech: "Healthcare",
  cat_engineering: "SaaS & Technology",
  cat_data_science: "SaaS & Technology",
  cat_cybersecurity: "SaaS & Technology",
  cat_iot: "SaaS & Technology",
  cat_robotics: "SaaS & Technology",
  cat_blockchain: "SaaS & Technology",
  cat_gaming: "SaaS & Technology",
  cat_research: "SaaS & Technology",
  cat_aerospace: "SaaS & Technology",
  cat_construction: "Construction",
  cat_telecom: "Telecommunications",
  cat_real_estate: "Real Estate",
  cat_hr: "Enterprise Functions",
  cat_marketing: "Enterprise Functions",
  cat_support: "Enterprise Functions",
  cat_legal: "Enterprise Functions",
  cat_consulting: "Enterprise Functions",
  cat_operations: "Enterprise Functions",
  cat_manufacturing: "Enterprise Functions",
  cat_energy: "Enterprise Functions",
  cat_mining: "Enterprise Functions",
  cat_agriculture: "Enterprise Functions",
  cat_automotive: "Enterprise Functions",
  cat_environment: "Enterprise Functions",
  cat_education: "Enterprise Functions",
  cat_entertainment: "Enterprise Functions",
  cat_media: "Enterprise Functions",
  cat_government: "Enterprise Functions",
  cat_nonprofit: "Enterprise Functions",
  cat_hospitality: "Enterprise Functions",
  cat_travel: "Enterprise Functions",
  cat_sports: "Enterprise Functions",
  cat_fashion: "Enterprise Functions",
  cat_general: "Productivity & Workflow",
};

const formatAgentName = (name = '') => String(name ?? '');

const Marketplace = () => {
  const [agents, setAgents] = useState([]);
  const [voiceAgents, setVoiceAgents] = useState([]);
  const [myPrivateAgents, setMyPrivateAgents] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [hoveredIndustry, setHoveredIndustry] = useState(null);
  const [hoveredFunction, setHoveredFunction] = useState(null);
  const [loading, setLoading] = useState(true);

  // Build dynamic industry tree that merges custom agents under a "Custom" sub-category
  const industryTree = useMemo(() => {
    const tree = JSON.parse(JSON.stringify(INDUSTRY_TREE));
    const allCustom = [
      ...agents.filter(a => a.is_custom),
      ...myPrivateAgents,
    ];
    const customByIndustry = {};
    allCustom.forEach(agent => {
      const industry = CATEGORY_TO_INDUSTRY[agent.category_id] || "Enterprise Functions";
      if (!customByIndustry[industry]) customByIndustry[industry] = [];
      if (!customByIndustry[industry].includes(agent.name)) {
        customByIndustry[industry].push(agent.name);
      }
    });
    for (const [industry, agentNames] of Object.entries(customByIndustry)) {
      if (tree[industry]) {
        tree[industry]["Custom"] = agentNames;
      }
    }
    return tree;
  }, [agents, myPrivateAgents]);

  // Combined list of all agents for filtering (includes private custom agents)
  const allAgents = useMemo(() => {
    const ids = new Set(agents.map(a => a.id));
    const extra = myPrivateAgents.filter(a => !ids.has(a.id));
    return [...agents, ...extra];
  }, [agents, myPrivateAgents]);

  const { isAuthenticated, user, token } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const carouselRef = useRef(null);
  const dropdownRef = useRef(null);

  const TIER_HIERARCHY = {
    'free': 0,
    'pro': 1,
    'enterprise': 2,
    'enterprise_pro': 3
  };

  const isLocked = (agent) => {
    if (!agent) return false;
    // Private agents owned by the user are never locked to them
    if (agent.is_custom && !agent.is_public) return false;

    const userTier = (user?.plan || 'free').toLowerCase();
    const agentTier = (agent.tier || 'free').toLowerCase();
    return TIER_HIERARCHY[userTier] < TIER_HIERARCHY[agentTier];
  };


  useEffect(() => {
    fetchAgents();
    fetchVoiceAgents();
    fetchIntegrations();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await axios.get(`${API}/agents`);
      const data = response.data;
      const backendAgents = Array.isArray(data) ? data : Array.isArray(data?.agents) ? data.agents : [];

      // Also fetch user-created custom agents (pass token so backend returns caller's private agents too)
      let customAgents = [];
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const customResponse = await axios.get(`${API}/custom-agents`, { headers });
        customAgents = Array.isArray(customResponse.data) ? customResponse.data : [];
      } catch (e) {
        // Custom agents endpoint may not have any yet, that's fine
      }

      // Separate private custom agents (creator's own, not public) into My Agents section
      const publicCustomAgents = customAgents.filter(a => a.is_public);
      const privateCustomAgents = customAgents.filter(a => a.is_custom && !a.is_public);
      setMyPrivateAgents(privateCustomAgents);

      setAgents([...backendAgents, ...publicCustomAgents]);
    } catch (err) {
      // setAgents(STATIC_AGENTS);
      console.log("No static agents")
    } finally {
      setLoading(false);
    }
  };

  const fetchVoiceAgents = async () => {
    try {
      const res = await axios.get(`${API}/voice-agents`);
      const data = res.data;
      setVoiceAgents(Array.isArray(data) ? data : data?.voice_agents || []);
    } catch { }
  };

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`);
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
      setIntegrations([
        { id: 1, name: 'Twilio', category: 'Communication', description: 'SMS & Voice automation for agent alerts.', status: 'COMING SOON' },
        { id: 2, name: 'Azure Services', category: 'Cloud', description: 'Enterprise cloud infrastructure and blob storage.', status: 'COMING SOON' },
        { id: 3, name: 'Dynamics 365', category: 'CRM', description: 'Unified business applications and ERP data.', status: 'COMING SOON' },
        { id: 4, name: 'Salesforce', category: 'CRM', description: 'Synchronize customer data across your AI agents.', status: 'COMING SOON' },
        { id: 5, name: 'Zoho', category: 'CRM', description: 'All-in-one CRM suite for sales, support, and operations.', status: 'COMING SOON' },
        { id: 6, name: 'HubSpot', category: 'Marketing', description: 'Inbound marketing, CRM, and sales automation platform.', status: 'COMING SOON' },
      ]);
    }
  };

  const deleteCustomAgent = async (agentId) => {
    setDeletingAgentId(agentId);
    try {
      await axios.delete(`${API}/custom-agents/${agentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMyPrivateAgents(prev => prev.filter(a => a.id !== agentId));
      toast.success('Agent deleted successfully');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete agent');
    } finally {
      setDeletingAgentId(null);
      setConfirmDeleteId(null);
    }
  };

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 340;
      carouselRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  const handleCardClick = (agent) => {
    if (isLocked(agent)) {
      navigate('/pricing');
      return;
    }

    if (agent.id === 'Precor-Support-Assistant') {
      navigate('/precor-chat');
    } else if (agent.id === 'autonomous-engineer') {
      navigate('/code-engineer');
    } else if (agent.id === 'real-estate') {
      navigate('/property-ai');
    } else if (agent.id === 'interior-design-ai') {
      navigate('/interior-design-ai');
    } else if (agent.id === 'vision-ai-agent' || agent.isVisionAgent) {
      navigate('/vision-agent');
    } else if (agent.isExternal && agent.externalUrl) {
      window.open(agent.externalUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(`/agent/${agent.id}`);
    }
  };

  const allCategories = [...new Set(agents.map(a => a.category))].sort();
  const generalList = allCategories.filter(cat => GENERAL_CATEGORIES.includes(cat));
  const specificList = allCategories.filter(cat => !GENERAL_CATEGORIES.includes(cat));

  const toggleDropdown = (name) => setActiveDropdown(activeDropdown === name ? null : name);
  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setActiveDropdown(null);
    if (category === 'All') {
      setSearchQuery('');
    }
  };

  const filteredAgents = allAgents.filter(agent => {
    const isDatabaseAgent =
      (agent?.name || '').toLowerCase().includes('database') ||
      (agent?.category || '').toLowerCase().includes('database');
    if (isDatabaseAgent) return false;

    const query = searchQuery.toLowerCase();
    const matchesSearch = !searchQuery || (agent.name || '').toLowerCase().includes(query) || (agent.description || '').toLowerCase().includes(query);

    let matchesCategory = false;
    if (selectedCategory === 'All') {
      matchesCategory = true;
    } else if (industryTree[selectedCategory]) {
      // It's an Industry
      const funcs = industryTree[selectedCategory];
      let inIndustry = false;
      for (const key in funcs) {
        if (funcs[key].includes(agent.name)) {
          inIndustry = true;
          break;
        }
      }
      matchesCategory = inIndustry;
    } else {
      // Check if it's a Function
      let isFunction = false;
      let inFunction = false;
      for (const ind in industryTree) {
        if (industryTree[ind][selectedCategory]) {
          isFunction = true;
          if (industryTree[ind][selectedCategory].includes(agent.name)) {
            inFunction = true;
          }
        }
      }
      if (isFunction) {
        matchesCategory = inFunction;
      } else {
        matchesCategory = agent.category === selectedCategory;
      }
    }

    return matchesSearch && matchesCategory;
  });

  // Split into accessible (sorted free-first) and locked premium agents
  const tierSort = (a, b) => (TIER_HIERARCHY[(a.tier || 'free').toLowerCase()] || 0) - (TIER_HIERARCHY[(b.tier || 'free').toLowerCase()] || 0);
  const accessibleAgents = filteredAgents.filter(a => !isLocked(a)).sort(tierSort);
  const premiumAgents = filteredAgents.filter(a => isLocked(a)).sort(tierSort);

  const nextRequiredTier = premiumAgents.length > 0
    ? (premiumAgents[0].tier || 'pro')
    : null;

  const getTierColor = (tier) => {
    switch (tier) {
      case 'free': return 'bg-green-100 text-green-700';
      case 'pro': return 'bg-blue-100 text-blue-700';
      case 'enterprise': return 'bg-purple-100 text-purple-700';
      case 'enterprise_pro': return 'bg-gradient-to-r from-purple-100 to-orange-100 text-purple-800';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getIcon = (name) => {
    const Icon = iconMap[name];
    return Icon ? <Icon className="w-6 h-6" /> : <Cpu className="w-6 h-6" />;
  };

  const getIntegrationIcon = (category) => {
    const Icon = integrationIconMap[category] || Puzzle;
    return <Icon className="w-6 h-6" />;
  };

  const marketplaceRightContent = isAuthenticated ? (
    <>
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9' }}>
        <Coins className="w-4 h-4 text-[#F87217]" />
        <span className="text-sm font-medium" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>{user?.credits || 0}</span>
      </div>
      <Button onClick={() => navigate('/dashboard')} className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm">Dashboard</Button>
    </>
  ) : (
    <Button onClick={() => navigate('/login')} className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm">Sign In</Button>
  );

  return (
    <div className="min-h-screen" style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}>
      <AppHeader rightContent={marketplaceRightContent} />

      {/* HEADER */}
      <div data-tour="marketplace-header" style={{ background: isDark ? '#0f1729' : '#fff', borderBottom: `1px solid ${isDark ? '#162040' : 'rgba(226,232,240,1)'}` }}>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
          <h1 className="text-2xl md:text-4xl font-bold font-['Outfit'] mb-4" style={{ color: isDark ? '#f0f6fc' : '#0d1a3a' }}>Agent Orchestration Platform</h1>
          <div className="flex items-center justify-between mb-4">
            <p className="text-slate-600 max-w-2xl">Discover 30+ specialized AI agents.<span className="font-semibold"> .</span></p>
            {/* {isAuthenticated && (
              <Button onClick={() => navigate('/builder')} className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Create Agent
              </Button>
            )} */}
          </div>

          <div className="flex flex-col gap-4">
            <div className="relative w-full max-w-3xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
              <input
                data-tour="marketplace-search"
                type="text"
                placeholder="Search agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-14 pl-12 pr-4 text-lg bg-white border border-slate-200 rounded-sm focus:ring-2 focus:ring-[#F87217] outline-none transition-all"
              />
            </div>

            <div className="flex flex-wrap items-center gap-4 mt-2" ref={dropdownRef}>
              <button onClick={() => handleCategorySelect('All')} className={`px-6 py-2.5 rounded-sm text-sm font-medium transition-all ${selectedCategory === 'All' ? 'bg-[#F87217] text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>All</button>

              {/* ── Cascading Industry Mega-Menu ── */}
              <div className="relative" data-tour="marketplace-category">

                {/* Trigger button */}
                <button
                  onClick={() => {
                    if (activeDropdown === 'industry') {
                      setActiveDropdown(null);
                      setHoveredIndustry(null);
                      setHoveredFunction(null);
                    } else {
                      setActiveDropdown('industry');
                    }
                  }}
                  className={`flex items-center gap-2 px-6 py-2.5 rounded-sm text-sm font-medium border transition-all ${activeDropdown === 'industry' || selectedCategory !== 'All'
                    ? 'border-[#F87217] text-[#F87217] bg-orange-50'
                    : isDark ? 'bg-[#0f1729] border-[#1e2d52] text-slate-300' : 'bg-white border-slate-200 text-slate-700'
                    }`}
                >
                  {selectedCategory !== 'All' ? selectedCategory : 'Select Industry'}
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* 3-panel mega menu */}
                {activeDropdown === 'industry' && (
                  <div
                    className="absolute top-full left-0 mt-2 flex shadow-2xl rounded-sm border z-30"
                    style={{
                      background: isDark ? '#0f1729' : '#fff',
                      borderColor: isDark ? '#1e2d52' : '#e2e8f0',
                      minWidth: '200px',
                    }}
                  >
                    {/* Panel 1 – Industries */}
                    <div
                      className="border-r py-1"
                      style={{
                        minWidth: '190px',
                        borderColor: isDark ? '#1e2d52' : '#e2e8f0',
                      }}
                    >
                      {Object.keys(industryTree).map((industry) => (
                        <button
                          key={industry}
                          className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors text-left ${hoveredIndustry === industry
                            ? 'bg-orange-50 text-[#F87217]'
                            : isDark ? 'text-slate-300 hover:bg-[#162040] hover:text-[#F87217]' : 'text-slate-700 hover:bg-orange-50 hover:text-[#F87217]'
                            }`}
                          onMouseEnter={() => { setHoveredIndustry(industry); setHoveredFunction(null); }}
                          onClick={() => { handleCategorySelect(industry); }}
                        >
                          <span>{industry}</span>
                          {Object.keys(industryTree[industry]).length > 0 && (
                            <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Panel 2 – Business Functions */}
                    {hoveredIndustry && (
                      <div
                        className="border-r py-1"
                        style={{
                          minWidth: '185px',
                          borderColor: isDark ? '#1e2d52' : '#e2e8f0',
                        }}
                      >
                        <p className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F87217] border-b mb-1" style={{ borderColor: isDark ? '#1e2d52' : '#e2e8f0' }}>
                          {hoveredIndustry}
                        </p>
                        {Object.keys(industryTree[hoveredIndustry]).map((fn) => (
                          <button
                            key={fn}
                            className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors text-left ${hoveredFunction === fn
                              ? 'bg-orange-50 text-[#F87217]'
                              : isDark ? 'text-slate-300 hover:bg-[#162040] hover:text-[#F87217]' : 'text-slate-700 hover:bg-orange-50 hover:text-[#F87217]'
                              }`}
                            onMouseEnter={() => setHoveredFunction(fn)}
                            onClick={() => { handleCategorySelect(fn); }}
                          >
                            <span>{fn}</span>
                            <ChevronRight className="w-3.5 h-3.5 opacity-50" />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Panel 3 – Agents */}
                    {hoveredIndustry && hoveredFunction && (
                      <div
                        className="py-1"
                        style={{ minWidth: '220px' }}
                      >
                        <p className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#F87217] border-b mb-1" style={{ borderColor: isDark ? '#1e2d52' : '#e2e8f0' }}>
                          {hoveredFunction}
                        </p>
                        {(industryTree[hoveredIndustry][hoveredFunction] || []).map((agentName) => (
                          <button
                            key={agentName}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${isDark ? 'text-slate-300 hover:bg-[#162040] hover:text-[#F87217]' : 'text-slate-700 hover:bg-orange-50 hover:text-[#F87217]'
                              }`}
                            onClick={() => {
                              setSearchQuery(agentName);
                              setActiveDropdown(null);
                              setHoveredIndustry(null);
                              setHoveredFunction(null);
                            }}
                          >
                            {agentName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {isAuthenticated && (
                <Button
                  onClick={() => navigate('/builder')}
                  className="rounded-sm flex items-center gap-2 text-white"
                  style={{
                    background: 'linear-gradient(135deg,#F87217,#FF9A4E)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'}`,
                    boxShadow: '0 6px 18px rgba(248,114,23,0.32)',
                  }}
                >
                  <Plus className="w-4 h-4" /> Create Agent
                </Button>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12 overflow-visible">
        {loading ? (
          <div className="text-center py-20 text-slate-500">Loading agents...</div>
        ) : (
          <div className="space-y-12">
            {/* ── Accessible agents ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible">
              {accessibleAgents.map((agent, agentIdx) => (
                <div
                  key={agent.id}
                  data-tour={agentIdx === 0 ? 'agent-card-first' : undefined}
                  className={`agent-card group relative border cursor-pointer transition-all duration-300 ease-out hover:border-[#F87217] hover:shadow-xl hover:-translate-y-1 overflow-hidden ${isLocked(agent) ? 'opacity-80' : ''}`}
                  style={{ minHeight: '220px', background: isDark ? '#0f1729' : '#fff', borderColor: isDark ? '#162040' : 'rgba(226,232,240,1)' }}
                  onClick={() => handleCardClick(agent)}
                >

                  {/* ── Base card content ── */}
                  <div className="p-6 h-full flex flex-col">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center transition-colors duration-300 group-hover:bg-[#F87217]/10">
                        <span className="text-slate-600 transition-colors duration-300 group-hover:text-[#F87217]">
                          {isLocked(agent) ? <Lock className="w-6 h-6" /> : getIcon(agent.icon)}
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge className={`${getTierColor(agent.tier)} border-0 rounded-sm uppercase text-[10px]`}>{agent.tier}</Badge>
                        {isLocked(agent) && (
                          <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                            <Lock className="w-2.5 h-2.5" /> LOCKED
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[#F87217] font-medium mb-1">{agent.category}</p>
                    <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-2 flex items-center gap-2">
                      {formatAgentName(agent.name)}
                      {agent.isExternal && <ExternalLink className="w-4 h-4 text-slate-400" />}
                      {agent.isVisionAgent && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: 'linear-gradient(135deg,#F87217,#ff6b00)', color: '#fff', letterSpacing: '0.5px' }}>
                          VISION
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-slate-600 line-clamp-2 flex-1">{agent.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-4">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-1 text-sm text-slate-500">
                          {agent.isExternal
                            ? <span className="text-xs italic">Visit the application</span>
                            : <><Coins className="w-4 h-4" /><span>{agent.credits_per_query} credits/query</span></>}
                        </div>
                        {!agent.isExternal && (
                          <StarRating rating={agent.rating ?? 5} totalReviews={agent.totalReviews} size={13} />
                        )}
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#F87217] group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  </div>

                  {/* ── Slide-up reveal overlay ── */}
                  <div
                    className="absolute inset-0 flex flex-col justify-between
                    translate-y-full group-hover:translate-y-0
                    transition-transform duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                    style={{
                      background: isDark
                        ? 'linear-gradient(160deg,#1a0a00 0%,#0f1729 60%)'
                        : 'linear-gradient(160deg, #fff7f0 0%, #ffffff 60%)',
                      borderTop: '2px solid #F87217',
                    }}
                  >
                    {/* Top section */}
                    <div className="px-5 pt-5 pb-2">
                      {/* Category + name row */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-md bg-[#F87217]/10 flex items-center justify-center shrink-0">
                          <span className="text-[#F87217]" style={{ transform: 'scale(0.8)' }}>{getIcon(agent.icon)}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{agent.category}</p>
                          <p className="text-sm font-bold text-slate-900 leading-tight truncate">{formatAgentName(agent.name)}</p>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 mb-3">{agent.description}</p>

                      {/* Feature chips */}
                      {((agent.features && agent.features.length > 0) || (agent.use_cases && agent.use_cases.length > 0)) && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {(agent.features || agent.use_cases || []).slice(0, 3).map((item, i) => (
                            <span
                              key={i}
                              className="px-2 py-0.5 rounded-full bg-white text-slate-600 text-[10px] font-medium border border-slate-200 shadow-sm"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Star rating in overlay */}
                      {!agent.isExternal && (
                        <StarRating rating={agent.rating ?? 5} totalReviews={agent.totalReviews} size={13} className="mt-1" />
                      )}
                    </div>

                    {/* Bottom CTA row */}
                    <div className="px-5 py-4 flex items-center justify-between border-t border-orange-100">
                      <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
                        {agent.isExternal
                          ? <><ExternalLink className="w-3 h-3" /><span>External</span></>
                          : <><Coins className="w-3 h-3 text-[#F87217]" /><span>{agent.credits_per_query} credits / query</span></>}
                      </div>
                      {isLocked(agent) ? (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-slate-400 px-3 py-1.5 rounded-sm shadow-md">
                          Upgrade <Lock className="w-3 h-3" />
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#F87217] px-3 py-1.5 rounded-sm shadow-md shadow-orange-200 hover:bg-[#C65B12] transition-colors">
                          {agent.isExternal ? 'Visit' : 'Open'} <ArrowRight className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ── Premium / Locked agents ── */}
            {premiumAgents.length > 0 && (
              <div>
                {/* Divider */}
                <div className="flex items-center gap-4 mb-8 mt-4">
                  <div className={`flex-1 h-px ${isDark ? 'bg-[#1e2d52]' : 'bg-slate-200'}`} />
                  <span className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest border ${isDark ? 'bg-[#0f1729] border-[#1e2d52] text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                    <Lock className="w-3 h-3" /> Premium
                  </span>
                  <div className={`flex-1 h-px ${isDark ? 'bg-[#1e2d52]' : 'bg-slate-200'}`} />
                </div>

                {/* Banner */}
                <div className={`rounded-xl border mb-8 px-6 py-5 flex items-center justify-between gap-4 ${isDark ? 'bg-[#1a1a2e] border-[#F87217]/20' : 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200'}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#F87217]/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-5 h-5 text-[#F87217]" />
                    </div>
                    <div>
                      <h2 className={`text-lg font-bold font-['Outfit'] ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Premium Agents
                      </h2>
                      <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                        Upgrade to <span className="font-semibold capitalize text-[#F87217]">{nextRequiredTier}</span> or higher to unlock {premiumAgents.length} agent{premiumAgents.length !== 1 ? 's' : ''}.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/pricing')}
                    className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 bg-[#F87217] hover:bg-[#C65B12] text-white text-sm font-semibold rounded-lg transition-colors shadow-md shadow-orange-200"
                  >
                    <Zap className="w-4 h-4" /> Upgrade Plan
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-visible">
                  {premiumAgents.map((agent) => (
                    <div
                      key={agent.id}
                      className={`agent-card group relative border cursor-pointer transition-all duration-300 ease-out hover:border-[#F87217] hover:shadow-xl hover:-translate-y-1 overflow-hidden opacity-70`}
                      style={{ minHeight: '220px', background: isDark ? '#0f1729' : '#fff', borderColor: isDark ? '#162040' : 'rgba(226,232,240,1)' }}
                      onClick={() => handleCardClick(agent)}
                    >
                      <div className="p-6 h-full flex flex-col">
                        <div className="flex items-start justify-between mb-4">
                          <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center">
                            <Lock className="w-6 h-6 text-slate-400" />
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Badge className={`${getTierColor(agent.tier)} border-0 rounded-sm uppercase text-[10px]`}>{agent.tier}</Badge>
                            <span className="text-[10px] font-bold text-slate-400 flex items-center gap-1"><Lock className="w-2.5 h-2.5" /> LOCKED</span>
                          </div>
                        </div>
                        <p className="text-xs text-[#F87217] font-medium mb-1">{agent.category}</p>
                        <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-2">{formatAgentName(agent.name)}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 flex-1">{agent.description}</p>
                        <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-4">
                          <span className="text-xs text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" /> Requires {agent.tier} plan</span>
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#F87217] px-3 py-1.5 rounded-sm">
                            Upgrade <Zap className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* My Agents — private custom agents (only visible to creator) */}
      {isAuthenticated && myPrivateAgents.length > 0 && (
        <div className="bg-gradient-to-b py-16 border-t" style={{ background: isDark ? '#0f1729' : '#f8fafc', borderColor: isDark ? '#162040' : '#e2e8f0' }}>
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] flex items-center gap-2">
                  <Cpu className="w-6 h-6 text-[#F87217]" />
                  My Agents
                </h2>
                <p className="text-slate-500 text-sm mt-1">
                  Your private custom agents — make them public to share with everyone
                </p>
              </div>
              <Button
                onClick={() => navigate('/builder')}
                className="bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Agent
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {myPrivateAgents.map((agent) => (
                <div key={agent.id} className="agent-card group bg-white border border-slate-200 hover:border-[#F87217] p-6 cursor-pointer transition-all relative" onClick={() => handleCardClick(agent)}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-slate-100 group-hover:bg-[#F87217]/10 rounded-sm flex items-center justify-center transition-colors">
                      <span className="text-slate-600 group-hover:text-[#F87217] transition-colors">{getIcon(agent.icon)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-slate-100 text-slate-600 border-0 rounded-sm">Private</Badge>
                      {confirmDeleteId === agent.id ? (
                        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => deleteCustomAgent(agent.id)}
                            disabled={deletingAgentId === agent.id}
                            className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {deletingAgentId === agent.id ? 'Deleting…' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-xs px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-sm font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={e => { e.stopPropagation(); setConfirmDeleteId(agent.id); }}
                          className="p-1.5 rounded-sm text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete agent"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-[#F87217] font-medium mb-1">{agent.category}</p>
                  <h3 className="text-lg font-semibold text-slate-900 font-['Outfit'] mb-2 group-hover:text-[#F87217]">
                    {formatAgentName(agent.name)}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{agent.description}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Coins className="w-4 h-4" /><span>{agent.credits_per_query} credits/query</span>
                      </div>
                      <StarRating rating={agent.rating ?? 5} totalReviews={agent.totalReviews} />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#F87217] group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Voice Agents section temporarily hidden */}


      {/* ── INTEGRATIONS SECTION ───────────────────────────────── */}
      <section style={{ background: isDark ? '#0f1729' : '#fff', borderTop: `1px solid ${isDark ? '#162040' : '#e2e8f0'}` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 md:mb-12 gap-6">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4">
                <span className="p-2 bg-orange-100 rounded-lg">
                  <Puzzle className="w-5 h-5 text-[#F87217]" />
                </span>
                <span className="text-sm font-bold text-[#F87217] uppercase tracking-wider">
                  Connectors
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Outfit'] mb-4">
                Integrations
              </h2>

              <p className="text-lg text-slate-600">
                Bridge your AI agents with third-party enterprise tools. Automate workflows across your entire infrastructure.
              </p>
            </div>

            <Button
              variant="outline"
              className="border-slate-200 hover:border-[#F87217] hover:text-[#F87217] flex gap-2 items-center rounded-md transition-all duration-300 hover:shadow-md"
            >
              <Link2 className="w-4 h-4" /> View Documentation
            </Button>
          </div>

          {/* Cards — responsive: horizontal scroll on mobile, grid on md+ */}
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-6 gap-4" style={{ overflow: 'visible' }}>
            {integrations.map((tool) => (
              <div
                key={tool.id}
                className="
                  group relative flex-shrink-0 w-52 p-5 bg-white border border-slate-200 rounded-xl
                  transition-all duration-300 ease-out z-0
                  hover:-translate-y-3
                  hover:scale-[1.04]
                  hover:shadow-2xl
                  hover:shadow-orange-200/40
                  hover:border-[#F87217]
                  hover:z-10
                "
              >
                {/* Soft Hover Glow */}
                <div
                  className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{ background: 'radial-gradient(circle at 50% 0%, rgba(248,114,23,0.15), transparent 60%)' }}
                />

                {/* Top Section — logo + badge */}
                <div className="relative z-10 flex justify-between items-start mb-4">
                  <div
                    className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center transition-all duration-300 shadow-sm group-hover:shadow-md group-hover:bg-white group-hover:scale-110 overflow-hidden"
                  >
                    {integrationLogoMap[tool.name] ? (
                      <img
                        src={integrationLogoMap[tool.name]}
                        alt={tool.name}
                        className="w-8 h-8 object-contain"
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div
                      className="w-8 h-8 rounded-lg bg-slate-200 items-center justify-center text-slate-400 text-xs font-bold"
                      style={{ display: integrationLogoMap[tool.name] ? 'none' : 'flex' }}
                    >
                      {tool.name.charAt(0)}
                    </div>
                  </div>

                  <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                    Coming Soon
                  </span>
                </div>

                {/* Content */}
                <h3 className="relative z-10 font-bold text-slate-900 text-sm mb-0.5 transition-colors duration-300 group-hover:text-[#F87217]">
                  {tool.name}
                </h3>

                <p className="relative z-10 text-[10px] font-medium text-slate-400 mb-2 uppercase tracking-wide">
                  {tool.category}
                </p>

                <p className="relative z-10 text-xs text-slate-500 line-clamp-2 mb-4 leading-relaxed">
                  {tool.description}
                </p>

                {/* Button */}
                <button
                  className="
                    relative z-10 w-full py-2 text-xs font-semibold rounded-lg border
                    border-slate-200 bg-slate-50 text-slate-700
                    transition-all duration-300
                    group-hover:bg-gradient-to-r
                    group-hover:from-[#F87217]
                    group-hover:to-[#FF9A4E]
                    group-hover:text-white
                    group-hover:border-transparent
                    group-hover:shadow-lg
                    group-hover:shadow-orange-200/50
                  "
                >
                  Connect Tool
                </button>
              </div>
            ))}
          </div>

          {/* Mobile horizontal scroll for integrations */}
          <div className="flex md:hidden gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory -mx-4 px-4">
            {integrations.map((tool) => (
              <div
                key={`mob-${tool.id}`}
                className="
                  flex-shrink-0 w-52 snap-start p-5 bg-white border border-slate-200 rounded-xl
                  transition-all duration-300
                "
              >
                {/* Logo + badge */}
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center overflow-hidden">
                    {integrationLogoMap[tool.name] ? (
                      <img src={integrationLogoMap[tool.name]} alt={tool.name} className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center text-slate-400 text-xs font-bold">
                        {tool.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full uppercase tracking-wide">Coming Soon</span>
                </div>
                <h3 className="font-bold text-slate-900 text-sm mb-0.5">{tool.name}</h3>
                <p className="text-[10px] font-medium text-slate-400 mb-2 uppercase tracking-wide">{tool.category}</p>
                <p className="text-xs text-slate-500 line-clamp-2 mb-3 leading-relaxed">{tool.description}</p>
                <button className="w-full py-2 text-xs font-semibold rounded-lg border border-slate-200 bg-slate-50 text-slate-700">
                  Connect Tool
                </button>
              </div>
            ))}
          </div>

          {/* Security Row */}
          <div className="mt-12 md:mt-16 pt-8 border-t border-slate-100 flex flex-wrap items-center justify-center gap-x-12 gap-y-6 opacity-40 grayscale">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              <span className="text-sm font-bold">SOC2 Certified</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-bold">GDPR Ready</span>
            </div>
            <div className="flex items-center gap-2">
              <Scale className="w-5 h-5" />
              <span className="text-sm font-bold">HIPAA Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              <span className="text-sm font-bold">AES-256 Encryption</span>
            </div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default Marketplace;